import React, { useRef, useEffect, useCallback, useState } from 'react';

// ======================== Types ========================

interface Point {
  x: number;
  y: number;
}

interface Annotation {
  id: string;
  chartId: string;
  tool: 'pen' | 'arrow' | 'text' | 'rect';
  color: string;
  points: Point[]; // pen: stroke path; arrow: [start, end]; rect: [topLeft, bottomRight]; text: [position]
  text?: string;
}

interface AnnotationStore {
  annotations: Record<string, Annotation[]>; // keyed by chartId
  activeTool: 'pen' | 'arrow' | 'text' | 'rect' | 'eraser';
  activeColor: string;
}

type AnnotationAction =
  | { type: 'SET_TOOL'; tool: AnnotationStore['activeTool'] }
  | { type: 'SET_COLOR'; color: string }
  | { type: 'ADD_ANNOTATION'; chartId: string; annotation: Annotation }
  | { type: 'REMOVE_ANNOTATION'; chartId: string; id: string }
  | { type: 'CLEAR_CHART'; chartId: string }
  | { type: 'LOAD_STORE'; annotations: Record<string, Annotation[]> };

// ======================== Reducer ========================

export const initialAnnotationStore: AnnotationStore = {
  annotations: {},
  activeTool: 'pen',
  activeColor: '#df1b41',
};

export function annotationReducer(state: AnnotationStore, action: AnnotationAction): AnnotationStore {
  switch (action.type) {
    case 'SET_TOOL':
      return { ...state, activeTool: action.tool };
    case 'SET_COLOR':
      return { ...state, activeColor: action.color };
    case 'ADD_ANNOTATION': {
      const existing = state.annotations[action.chartId] || [];
      return {
        ...state,
        annotations: {
          ...state.annotations,
          [action.chartId]: [...existing, action.annotation],
        },
      };
    }
    case 'REMOVE_ANNOTATION': {
      const list = state.annotations[action.chartId] || [];
      return {
        ...state,
        annotations: {
          ...state.annotations,
          [action.chartId]: list.filter(a => a.id !== action.id),
        },
      };
    }
    case 'CLEAR_CHART':
      return {
        ...state,
        annotations: { ...state.annotations, [action.chartId]: [] },
      };
    case 'LOAD_STORE':
      return { ...state, annotations: action.annotations || {} };
    default:
      return state;
  }
}

// ======================== Canvas Component ========================

function genId(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
}

interface AnnotationCanvasProps {
  chartRef: { current: any | null };
  chartId: string;
  store: AnnotationStore;
  dispatch: React.Dispatch<AnnotationAction>;
  enabled: boolean;
}

export const AnnotationCanvas: React.FC<AnnotationCanvasProps> = ({
  chartRef, chartId, store, dispatch, enabled,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawingRef = useRef(false);
  const pointsRef = useRef<Point[]>([]);

  const getAnnotations = useCallback(() => {
    return store.annotations[chartId] || [];
  }, [store.annotations, chartId]);

  // Render all annotations
  const renderAll = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * window.devicePixelRatio;
    canvas.height = rect.height * window.devicePixelRatio;
    ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    ctx.clearRect(0, 0, rect.width, rect.height);

    for (const ann of getAnnotations()) {
      ctx.strokeStyle = ann.color;
      ctx.fillStyle = ann.color;
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      if (ann.tool === 'pen' && ann.points.length > 1) {
        ctx.beginPath();
        ctx.moveTo(ann.points[0].x, ann.points[0].y);
        for (let i = 1; i < ann.points.length; i++) {
          ctx.lineTo(ann.points[i].x, ann.points[i].y);
        }
        ctx.stroke();
      } else if (ann.tool === 'arrow' && ann.points.length === 2) {
        const [start, end] = ann.points;
        const angle = Math.atan2(end.y - start.y, end.x - start.x);
        const headLen = 12;
        ctx.beginPath();
        ctx.moveTo(start.x, start.y);
        ctx.lineTo(end.x, end.y);
        ctx.stroke();
        // Arrowhead
        ctx.beginPath();
        ctx.moveTo(end.x, end.y);
        ctx.lineTo(end.x - headLen * Math.cos(angle - Math.PI / 6), end.y - headLen * Math.sin(angle - Math.PI / 6));
        ctx.lineTo(end.x - headLen * Math.cos(angle + Math.PI / 6), end.y - headLen * Math.sin(angle + Math.PI / 6));
        ctx.closePath();
        ctx.fill();
      } else if (ann.tool === 'rect' && ann.points.length === 2) {
        const [p1, p2] = ann.points;
        const x = Math.min(p1.x, p2.x);
        const y = Math.min(p1.y, p2.y);
        const w = Math.abs(p2.x - p1.x);
        const h = Math.abs(p2.y - p1.y);
        ctx.setLineDash([4, 4]);
        ctx.strokeRect(x, y, w, h);
        ctx.setLineDash([]);
      } else if (ann.tool === 'text' && ann.points.length === 1 && ann.text) {
        ctx.font = '13px Inter, sans-serif';
        ctx.fillStyle = ann.color;
        // Background
        const metrics = ctx.measureText(ann.text);
        const padding = 4;
        ctx.globalAlpha = 0.9;
        ctx.fillStyle = '#fff';
        ctx.fillRect(
          ann.points[0].x - padding,
          ann.points[0].y - 14,
          metrics.width + padding * 2,
          18 + padding,
        );
        ctx.globalAlpha = 1;
        ctx.fillStyle = ann.color;
        ctx.fillText(ann.text, ann.points[0].x, ann.points[0].y);
      }
    }
  }, [getAnnotations]);

  // Re-render when annotations change
  useEffect(() => {
    renderAll();
  }, [renderAll, store.annotations]);

  // Re-render on resize
  useEffect(() => {
    const handleResize = () => renderAll();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [renderAll]);

  const getPos = (e: React.MouseEvent): Point => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!enabled) return;
    const pos = getPos(e);

    if (store.activeTool === 'eraser') {
      // Find and remove nearest annotation
      const anns = getAnnotations();
      for (let i = anns.length - 1; i >= 0; i--) {
        const ann = anns[i];
        const hit = ann.points.some(p =>
          Math.abs(p.x - pos.x) < 15 && Math.abs(p.y - pos.y) < 15
        );
        if (hit) {
          dispatch({ type: 'REMOVE_ANNOTATION', chartId, id: ann.id });
          return;
        }
      }
      return;
    }

    if (store.activeTool === 'text') {
      const text = prompt('输入标注文字:');
      if (text) {
        dispatch({
          type: 'ADD_ANNOTATION', chartId,
          annotation: {
            id: genId(), chartId, tool: 'text',
            color: store.activeColor, points: [pos], text,
          },
        });
      }
      return;
    }

    drawingRef.current = true;
    pointsRef.current = [pos];
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!drawingRef.current || !enabled) return;
    const pos = getPos(e);

    if (store.activeTool === 'pen') {
      pointsRef.current.push(pos);
      // Live preview
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const pts = pointsRef.current;
      if (pts.length < 2) return;
      ctx.strokeStyle = store.activeColor;
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(pts[pts.length - 2].x * window.devicePixelRatio, pts[pts.length - 2].y * window.devicePixelRatio);
      ctx.lineTo(pts[pts.length - 1].x * window.devicePixelRatio, pts[pts.length - 1].y * window.devicePixelRatio);
      ctx.stroke();
    } else {
      // Arrow / Rect: just update endpoint, re-render preview
      pointsRef.current = [pointsRef.current[0], pos];
      renderAll();
      // Draw preview of current shape
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.strokeStyle = store.activeColor;
      ctx.fillStyle = store.activeColor;
      ctx.lineWidth = 2;
      const [start, end] = pointsRef.current;
      if (store.activeTool === 'arrow') {
        const angle = Math.atan2(end.y - start.y, end.x - start.x);
        ctx.beginPath();
        ctx.moveTo(start.x, start.y);
        ctx.lineTo(end.x, end.y);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(end.x, end.y);
        ctx.lineTo(end.x - 12 * Math.cos(angle - Math.PI / 6), end.y - 12 * Math.sin(angle - Math.PI / 6));
        ctx.lineTo(end.x - 12 * Math.cos(angle + Math.PI / 6), end.y - 12 * Math.sin(angle + Math.PI / 6));
        ctx.closePath();
        ctx.fill();
      } else if (store.activeTool === 'rect') {
        ctx.setLineDash([4, 4]);
        ctx.strokeRect(
          Math.min(start.x, end.x), Math.min(start.y, end.y),
          Math.abs(end.x - start.x), Math.abs(end.y - start.y),
        );
        ctx.setLineDash([]);
      }
    }
  };

  const handleMouseUp = () => {
    if (!drawingRef.current || !enabled) return;
    drawingRef.current = false;
    const pts = pointsRef.current;
    if (pts.length < 2) return;

    const tool = store.activeTool as 'pen' | 'arrow' | 'rect';
    dispatch({
      type: 'ADD_ANNOTATION', chartId,
      annotation: {
        id: genId(), chartId, tool,
        color: store.activeColor,
        points: tool === 'pen' ? [...pts] : [pts[0], pts[pts.length - 1]],
      },
    });
    pointsRef.current = [];
  };

  if (!enabled && getAnnotations().length === 0) return null;

  return React.createElement('canvas', {
    ref: canvasRef,
    onMouseDown: handleMouseDown,
    onMouseMove: handleMouseMove,
    onMouseUp: handleMouseUp,
    onMouseLeave: handleMouseUp,
    style: {
      position: 'absolute',
      inset: 0,
      width: '100%',
      height: '100%',
      cursor: enabled
        ? store.activeTool === 'eraser' ? 'crosshair'
        : store.activeTool === 'text' ? 'text'
        : 'crosshair'
        : 'default',
      pointerEvents: enabled ? 'auto' : 'none',
      zIndex: enabled ? 10 : 1,
    },
  });
};

// ======================== Toolbar Component ========================

interface AnnotationToolbarProps {
  store: AnnotationStore;
  dispatch: React.Dispatch<AnnotationAction>;
  chartId: string;
  chartRef: { current: any | null };
}

const tools: Array<{ key: AnnotationStore['activeTool']; label: string; icon: string }> = [
  { key: 'pen', label: '画笔', icon: '✏️' },
  { key: 'arrow', label: '箭头', icon: '➡️' },
  { key: 'rect', label: '框选', icon: '⬜' },
  { key: 'text', label: '文字', icon: 'T' },
  { key: 'eraser', label: '擦除', icon: '🧹' },
];

const colors = ['#df1b41', '#533afd', '#15be53', '#e6a200', '#0073e6', '#061b31'];

export const AnnotationToolbar: React.FC<AnnotationToolbarProps> = ({
  store, dispatch, chartId,
}) => {
  const annCount = (store.annotations[chartId] || []).length;

  return React.createElement('div', {
    style: {
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      padding: '6px 10px',
      background: 'var(--bg-subtle, #f8fafc)',
      border: '1px solid var(--border-color, #e5edf5)',
      borderRadius: 6,
      marginTop: 8,
      flexWrap: 'wrap' as const,
    },
  },
    // Tool buttons
    ...tools.map(t =>
      React.createElement('button', {
        key: t.key,
        title: t.label,
        onClick: () => dispatch({ type: 'SET_TOOL', tool: t.key }),
        style: {
          width: 28, height: 28,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          border: store.activeTool === t.key ? '2px solid var(--primary, #533afd)' : '1px solid var(--border-color, #e5edf5)',
          borderRadius: 4,
          background: store.activeTool === t.key ? 'rgba(83,58,253,0.08)' : 'var(--card-bg, #fff)',
          cursor: 'pointer',
          fontSize: t.key === 'text' ? 13 : 14,
          fontWeight: t.key === 'text' ? 700 : 400,
          color: 'var(--text-primary)',
          padding: 0,
        },
      }, t.icon)
    ),
    // Separator
    React.createElement('div', {
      style: { width: 1, height: 20, background: 'var(--border-color, #e5edf5)', margin: '0 4px' },
    }),
    // Color buttons
    ...colors.map(c =>
      React.createElement('button', {
        key: c,
        onClick: () => dispatch({ type: 'SET_COLOR', color: c }),
        style: {
          width: 18, height: 18,
          borderRadius: '50%',
          border: store.activeColor === c ? '2px solid var(--text-primary)' : '2px solid transparent',
          background: c,
          cursor: 'pointer',
          padding: 0,
        },
      })
    ),
    // Separator
    React.createElement('div', {
      style: { width: 1, height: 20, background: 'var(--border-color, #e5edf5)', margin: '0 4px' },
    }),
    // Clear button
    React.createElement('button', {
      onClick: () => dispatch({ type: 'CLEAR_CHART', chartId }),
      disabled: annCount === 0,
      style: {
        fontSize: 11, padding: '3px 8px',
        border: '1px solid var(--border-color, #e5edf5)',
        borderRadius: 4,
        background: 'var(--card-bg, #fff)',
        color: annCount > 0 ? 'var(--danger, #df1b41)' : 'var(--text-muted)',
        cursor: annCount > 0 ? 'pointer' : 'default',
        opacity: annCount > 0 ? 1 : 0.5,
      },
    }, `清除 (${annCount})`),
  );
};
